import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  Video, Eye, Users, Clock, TrendingUp, Play, Heart, 
  MessageCircle, BarChart3, Calendar, ChevronDown, Loader2,
  Film, Activity, Timer
} from 'lucide-react';
import { 
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, LineChart, Line
} from 'recharts';

const API = process.env.REACT_APP_BACKEND_URL || '';

const CHART_COLORS = ['#8b5cf6', '#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#ec4899', '#06b6d4', '#84cc16'];

const ReelsAnalyticsTab = ({ authToken }) => {
  const [loading, setLoading] = useState(true);
  const [analytics, setAnalytics] = useState(null);
  const [timeRange, setTimeRange] = useState('30d');
  const [selectedReel, setSelectedReel] = useState(null);
  const [reelDetails, setReelDetails] = useState(null);
  const [loadingDetails, setLoadingDetails] = useState(false);

  // Fetch analytics data
  useEffect(() => {
    const fetchAnalytics = async () => {
      setLoading(true);
      try {
        const res = await axios.get(`${API}/api/reels/analytics/dashboard?range=${timeRange}`, {
          headers: { Authorization: `Bearer ${authToken}` }
        });
        setAnalytics(res.data);
      } catch (err) {
        console.error('Error fetching reels analytics:', err);
      } finally {
        setLoading(false);
      }
    };

    if (authToken) {
      fetchAnalytics();
    }
  }, [authToken, timeRange]);

  // Fetch single reel details
  const fetchReelDetails = async (reelId) => {
    setLoadingDetails(true);
    try {
      const res = await axios.get(`${API}/api/reels/analytics/reel/${reelId}?range=${timeRange}`, {
        headers: { Authorization: `Bearer ${authToken}` }
      });
      setReelDetails(res.data);
    } catch (err) {
      console.error('Error fetching reel details:', err);
    } finally {
      setLoadingDetails(false);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds) return '0:00';
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const formatWatchTime = (seconds) => {
    if (!seconds) return '0m';
    const hours = Math.floor(seconds / 3600);
    const mins = Math.floor((seconds % 3600) / 60);
    if (hours > 0) {
      return `${hours}h ${mins}m`;
    }
    return `${mins}m`;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-20">
        <Loader2 className="h-8 w-8 animate-spin text-purple-500" />
        <span className="ml-3 text-gray-400">Loading reels analytics...</span>
      </div>
    );
  }

  if (!analytics) {
    return (
      <div className="text-center py-20 text-gray-400">
        <Film className="h-16 w-16 mx-auto mb-4 opacity-50" />
        <p>No reels analytics data available</p>
      </div>
    );
  }

  const { summary, trending_reels, reel_performance, daily_views } = analytics;

  return (
    <div className="space-y-6">
      {/* Time Range Selector */}
      <div className="flex justify-between items-center">
        <h2 className="text-xl font-bold text-white flex items-center gap-2">
          <Film className="h-6 w-6 text-purple-400" />
          Reels Analytics
        </h2>
        <select
          value={timeRange}
          onChange={(e) => setTimeRange(e.target.value)}
          className="bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-purple-500"
        >
          <option value="2d">Last 2 Days</option>
          <option value="4d">Last 4 Days</option>
          <option value="7d">Last 7 Days</option>
          <option value="30d">Last 30 Days</option>
          <option value="90d">Last 90 Days</option>
          <option value="all">All Time</option>
        </select>
      </div>

      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
        <SummaryCard
          icon={<Film className="h-5 w-5" />}
          label="Total Reels"
          value={summary.total_reels}
          color="purple"
        />
        <SummaryCard
          icon={<Eye className="h-5 w-5" />}
          label="Total Views"
          value={summary.total_views?.toLocaleString()}
          subtext={`${summary.total_watches?.toLocaleString()} total watches`}
          color="blue"
        />
        <SummaryCard
          icon={<Users className="h-5 w-5" />}
          label="Unique Viewers"
          value={summary.unique_viewers?.toLocaleString()}
          color="green"
        />
        <SummaryCard
          icon={<Clock className="h-5 w-5" />}
          label="Watch Time"
          value={`${summary.total_watch_time_hours || 0}h`}
          subtext={`${summary.total_watch_time_minutes || 0} minutes`}
          color="yellow"
        />
        <SummaryCard
          icon={<Timer className="h-5 w-5" />}
          label="Avg Watch/Reel"
          value={formatWatchTime(summary.avg_watch_time_per_reel_seconds)}
          color="pink"
        />
        <SummaryCard
          icon={<Activity className="h-5 w-5" />}
          label="Avg Views/Reel"
          value={summary.avg_views_per_reel?.toFixed(1)}
          color="cyan"
        />
      </div>

      {/* Charts Row */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Daily Views Chart */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 p-4">
          <h3 className="font-bold text-white mb-4 flex items-center gap-2">
            <BarChart3 className="h-5 w-5 text-blue-400" />
            Daily Views
          </h3>
          <div className="h-64">
            {daily_views?.length > 0 ? (
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={daily_views}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis 
                    dataKey="date" 
                    stroke="#9ca3af"
                    tick={{ fill: '#9ca3af', fontSize: 10 }}
                    tickFormatter={(val) => {
                      const date = new Date(val);
                      return date.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
                    }}
                  />
                  <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                  <Tooltip
                    contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                    labelStyle={{ color: '#fff' }}
                    itemStyle={{ color: '#8b5cf6' }}
                  />
                  <Bar dataKey="views" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            ) : (
              <div className="h-full flex items-center justify-center text-gray-500">
                No view data for this period
              </div>
            )}
          </div>
        </div>

        {/* Trending Reels */}
        <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
          <div className="p-4 border-b border-gray-800">
            <h3 className="font-bold text-white flex items-center gap-2">
              <TrendingUp className="h-5 w-5 text-green-400" />
              Trending Reels
            </h3>
          </div>
          <div className="p-4 space-y-3 max-h-64 overflow-y-auto">
            {trending_reels?.length > 0 ? (
              trending_reels.slice(0, 6).map((reel, idx) => {
                const maxViews = Math.max(...trending_reels.map(r => r.views), 1);
                const width = (reel.views / maxViews) * 100;
                return (
                  <div 
                    key={reel.id} 
                    className="flex items-center gap-3 cursor-pointer hover:bg-gray-800/50 p-2 rounded-lg transition-colors"
                    onClick={() => {
                      setSelectedReel(reel);
                      fetchReelDetails(reel.id);
                    }}
                  >
                    <span className="text-lg font-bold text-gray-500 w-6">#{idx + 1}</span>
                    {reel.thumbnail_url && (
                      <img 
                        src={reel.thumbnail_url} 
                        alt={reel.title} 
                        className="w-12 h-16 object-cover rounded"
                      />
                    )}
                    <div className="flex-1 min-w-0">
                      <p className="text-sm text-white truncate">{reel.title || 'Untitled'}</p>
                      <div className="flex items-center gap-3 text-xs text-gray-400 mt-1">
                        <span className="flex items-center gap-1">
                          <Eye className="h-3 w-3" /> {reel.views}
                        </span>
                        <span className="flex items-center gap-1">
                          <Heart className="h-3 w-3" /> {reel.like_count}
                        </span>
                        <span className="flex items-center gap-1">
                          <Clock className="h-3 w-3" /> {formatWatchTime(reel.watch_time_seconds)}
                        </span>
                      </div>
                      <div className="mt-1 h-1.5 bg-gray-800 rounded-full overflow-hidden">
                        <div 
                          className="h-full rounded-full transition-all" 
                          style={{ width: `${width}%`, backgroundColor: CHART_COLORS[idx % CHART_COLORS.length] }}
                        />
                      </div>
                    </div>
                  </div>
                );
              })
            ) : (
              <div className="text-center text-gray-500 text-sm py-4">No trending reels</div>
            )}
          </div>
        </div>
      </div>

      {/* All Reels Performance Table */}
      <div className="bg-gray-900 rounded-xl border border-gray-800 overflow-hidden">
        <div className="p-4 border-b border-gray-800">
          <h3 className="font-bold text-white flex items-center gap-2">
            <Video className="h-5 w-5 text-purple-400" />
            All Reels Performance
          </h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead>
              <tr className="bg-gray-800/50">
                <th className="text-left px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Reel</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Views</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Unique Viewers</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Total Watches</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Watch Time</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Likes</th>
                <th className="text-center px-4 py-3 text-xs font-medium text-gray-400 uppercase tracking-wider">Comments</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-800">
              {reel_performance?.map((reel) => (
                <tr 
                  key={reel.id} 
                  className="hover:bg-gray-800/30 cursor-pointer transition-colors"
                  onClick={() => {
                    setSelectedReel(reel);
                    fetchReelDetails(reel.id);
                  }}
                >
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {reel.thumbnail_url && (
                        <img 
                          src={reel.thumbnail_url} 
                          alt={reel.title} 
                          className="w-10 h-14 object-cover rounded"
                        />
                      )}
                      <div>
                        <p className="text-sm text-white font-medium truncate max-w-[200px]">{reel.title || 'Untitled'}</p>
                        <p className="text-xs text-gray-500">{formatDuration(reel.duration)}</p>
                      </div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-white font-medium">{reel.views?.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-gray-300">{reel.unique_viewers?.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-gray-300">{reel.total_watches?.toLocaleString()}</span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <div className="text-gray-300">
                      <div>{reel.watch_time_hours}h {reel.watch_time_minutes % 60}m</div>
                      <div className="text-xs text-gray-500">{Math.round(reel.watch_time_seconds)}s total</div>
                    </div>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-pink-400 flex items-center justify-center gap-1">
                      <Heart className="h-3 w-3" /> {reel.like_count}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-center">
                    <span className="text-blue-400 flex items-center justify-center gap-1">
                      <MessageCircle className="h-3 w-3" /> {reel.comment_count}
                    </span>
                  </td>
                </tr>
              ))}
              {(!reel_performance || reel_performance.length === 0) && (
                <tr>
                  <td colSpan="7" className="px-4 py-8 text-center text-gray-500">
                    No reels data available
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Reel Detail Modal */}
      {selectedReel && (
        <ReelDetailModal
          reel={selectedReel}
          details={reelDetails}
          loading={loadingDetails}
          onClose={() => { setSelectedReel(null); setReelDetails(null); }}
          formatDuration={formatDuration}
          formatWatchTime={formatWatchTime}
        />
      )}
    </div>
  );
};

// Summary Card Component
const SummaryCard = ({ icon, label, value, subtext, color }) => {
  const colorClasses = {
    purple: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
    blue: 'bg-blue-500/20 text-blue-400 border-blue-500/30',
    green: 'bg-green-500/20 text-green-400 border-green-500/30',
    yellow: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
    pink: 'bg-pink-500/20 text-pink-400 border-pink-500/30',
    cyan: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  };

  return (
    <div className={`rounded-xl border p-4 ${colorClasses[color]}`}>
      <div className="flex items-center gap-2 mb-2">
        {icon}
        <span className="text-xs text-gray-400 uppercase tracking-wider">{label}</span>
      </div>
      <div className="text-2xl font-bold text-white">{value}</div>
      {subtext && <div className="text-xs text-gray-500 mt-1">{subtext}</div>}
    </div>
  );
};

// Reel Detail Modal Component
const ReelDetailModal = ({ reel, details, loading, onClose, formatDuration, formatWatchTime }) => {
  return (
    <div className="fixed inset-0 bg-black/70 flex items-center justify-center z-50 p-4" onClick={onClose}>
      <div 
        className="bg-gray-900 rounded-xl border border-gray-700 max-w-2xl w-full max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="p-6 border-b border-gray-800">
          <div className="flex items-start gap-4">
            {reel.thumbnail_url && (
              <img 
                src={reel.thumbnail_url} 
                alt={reel.title} 
                className="w-24 h-32 object-cover rounded-lg"
              />
            )}
            <div className="flex-1">
              <h2 className="text-xl font-bold text-white">{reel.title || 'Untitled Reel'}</h2>
              <p className="text-sm text-gray-400 mt-1">Duration: {formatDuration(reel.duration)}</p>
              {reel.created_at && (
                <p className="text-xs text-gray-500 mt-1">
                  Created: {new Date(reel.created_at).toLocaleDateString()}
                </p>
              )}
            </div>
            <button 
              onClick={onClose}
              className="text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          </div>
        </div>

        {loading ? (
          <div className="p-8 text-center">
            <Loader2 className="h-8 w-8 animate-spin text-purple-500 mx-auto" />
            <p className="text-gray-400 mt-2">Loading analytics...</p>
          </div>
        ) : details ? (
          <div className="p-6 space-y-6">
            {/* Stats Grid */}
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                <Eye className="h-5 w-5 mx-auto text-blue-400 mb-2" />
                <div className="text-2xl font-bold text-white">{details.analytics?.total_views}</div>
                <div className="text-xs text-gray-400">Total Views</div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                <Users className="h-5 w-5 mx-auto text-green-400 mb-2" />
                <div className="text-2xl font-bold text-white">{details.analytics?.unique_viewers}</div>
                <div className="text-xs text-gray-400">Unique Viewers</div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                <Play className="h-5 w-5 mx-auto text-purple-400 mb-2" />
                <div className="text-2xl font-bold text-white">{details.analytics?.total_watches}</div>
                <div className="text-xs text-gray-400">Total Watches</div>
              </div>
              <div className="bg-gray-800/50 rounded-lg p-4 text-center">
                <Clock className="h-5 w-5 mx-auto text-yellow-400 mb-2" />
                <div className="text-2xl font-bold text-white">{formatWatchTime(details.analytics?.total_watch_time_seconds)}</div>
                <div className="text-xs text-gray-400">Watch Time</div>
              </div>
            </div>

            {/* Additional Stats */}
            <div className="grid grid-cols-2 gap-4">
              <div className="bg-gray-800/30 rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-1">Avg Watch Time/View</div>
                <div className="text-lg font-bold text-white">
                  {details.analytics?.avg_watch_time_per_view?.toFixed(1)}s
                </div>
              </div>
              <div className="bg-gray-800/30 rounded-lg p-4">
                <div className="text-sm text-gray-400 mb-1">Rewatch Rate</div>
                <div className="text-lg font-bold text-white">
                  {details.analytics?.rewatch_rate?.toFixed(1)}%
                </div>
              </div>
            </div>

            {/* Daily Views Chart */}
            {details.daily_views?.length > 0 && (
              <div>
                <h3 className="text-sm font-medium text-gray-400 mb-3">Daily Views</h3>
                <div className="h-40">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={details.daily_views}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                      <XAxis 
                        dataKey="date" 
                        stroke="#9ca3af"
                        tick={{ fill: '#9ca3af', fontSize: 10 }}
                        tickFormatter={(val) => new Date(val).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                      />
                      <YAxis stroke="#9ca3af" tick={{ fill: '#9ca3af', fontSize: 10 }} />
                      <Tooltip
                        contentStyle={{ backgroundColor: '#1f2937', border: '1px solid #374151', borderRadius: '8px' }}
                      />
                      <Line type="monotone" dataKey="views" stroke="#8b5cf6" strokeWidth={2} dot={{ fill: '#8b5cf6' }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </div>
            )}
          </div>
        ) : (
          <div className="p-8 text-center text-gray-500">
            No detailed analytics available
          </div>
        )}
      </div>
    </div>
  );
};

export default ReelsAnalyticsTab;
